<template>
  <div class="d-flex flex-column align-items-center justify-content-center h-100">
    <div class="mel-glass-panel p-4 text-center" style="max-width: 400px;">
      <div v-if="isProcessing" class="mb-3">
        <div class="spinner-border text-primary" role="status"></div>
        <p class="mt-3">{{ $t('page.entra.processing') }}</p>
      </div>
      <div v-else-if="errorMessage" class="mb-3">
        <font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="text-danger fa-2x mb-3" />
        <p class="text-danger">{{ errorMessage }}</p>
        <button class="btn btn-primary" @click="router.push({ name: 'home' })">
          {{ $t('action.goHome') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { router } from '@/services';
import api from '@/services/api';
import { useAuthStore, useEntraLoginsStore } from '@/stores';
import type IAccessTokenResponse from '@/models/responses/IAccessTokenResponse';
import { useI18n } from 'vue-i18n';

const route = useRoute();
const authStore = useAuthStore();
const entraLoginsStore = useEntraLoginsStore();
const i18n = useI18n();

const isProcessing = ref(true);
const errorMessage = ref('');

onMounted(async () => {
  const code = route.query.code as string;
  const state = route.query.state as string;
  const error = route.query.error as string;

  if (error) {
    isProcessing.value = false;
    errorMessage.value = (route.query.error_description as string) || error;
    return;
  }

  if (!code || !state) {
    isProcessing.value = false;
    errorMessage.value = i18n.t('page.entra.missingParameters');
    return;
  }

  try {
    if (state.startsWith('login:')) {
      // Login flow
      const response = await api.post<IAccessTokenResponse>('v1.0/auth-entra/login', { code, state });
      await authStore.loginWithToken(response.data);
    } else if (state.startsWith('link:')) {
      // Linking flow (user is authenticated)
      await api.post('v1.0/auth-entra/callback', { code, state });
      await entraLoginsStore.fetchAll(true);
      router.push({ name: 'profile' });
    } else {
      errorMessage.value = i18n.t('page.entra.invalidState');
    }
  } catch (e: any) {
    errorMessage.value = e?.response?.data || e?.message || i18n.t('page.entra.unknownError');
  } finally {
    isProcessing.value = false;
  }
});
</script>
